import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { Send, X } from "lucide-react";
import { API_BASE_URL } from "../config/apiconfig";
import { useAuth } from "../contexts/AuthContext";




const AddCareerAdvice = () => {
    const navigate = useNavigate();
    const { user } = useAuth();
    const [formData, setFormData] = useState({
        title: "",
        description: "",
        content: "", // Changed from 'link' to 'content' to match the server
    });
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [error, setError] = useState("");

    const handleChange = (e) => {
        setFormData({
            ...formData,
            [e.target.name]: e.target.value
        });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsSubmitting(true);
        setError("");

        // Role guard: only admin or recruiter can add
        if (!(user?.role === 'recruiter' || user?.role === 'e2e-admin')) {
            setIsSubmitting(false);
            setError("You are not authorized to add articles.");
            return;
        }

        // Basic client-side validation
        const title = formData.title.trim();
        const description = formData.description.trim();
        const content = formData.content.trim();
        if (title.length < 5) {
            setIsSubmitting(false);
            setError("Title must be at least 5 characters.");
            return;
        }
        if (description.length < 10) {
            setIsSubmitting(false);
            setError("Description must be at least 10 characters.");
            return;
        }
        if (content.length < 20) {
            setIsSubmitting(false);
            setError("Content must be at least 20 characters.");
            return;
        }

        try {
            const response = await fetch(`${API_BASE_URL}/api/articles`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json", // Fixed header name
                },
                body: JSON.stringify(formData),
            });

            if (!response.ok) {
                throw new Error("Failed to create article");
            }

            navigate("/career-advice");
        } catch (err) {
            setError("Failed to create article. Please try again.");
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <>
            <Header />
            <div className="min-h-screen bg-gray-50 py-8">
                <div className="w-full px-4 sm:px-6 lg:px-8">
                    <div className="bg-white rounded-xl shadow-lg p-6 md:p-8">
                        <div className="flex justify-between items-center mb-6">
                            <h1 className="text-2xl md:text-3xl font-semibold text-gray-900">
                                Add Career Advice Article
                            </h1>
                            <button
                                onClick={() => navigate("/career-advice")}
                                className="p-2 hover:bg-gray-100 rounded-full transition-colors"
                            >
                                <X className="w-6 h-6 text-gray-500" />
                            </button>
                        </div>

                        {error && (
                            <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg text-red-600">
                                {error}
                            </div>
                        )}

                        <form onSubmit={handleSubmit} className="space-y-6">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Title
                                </label>
                                <input
                                    type="text"
                                    name="title"
                                    value={formData.title}
                                    onChange={handleChange}
                                    required
                                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors"
                                    placeholder="Enter article title"
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Description
                                </label>
                                <input
                                    type="text"
                                    name="description"
                                    value={formData.description}
                                    onChange={handleChange}
                                    required
                                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors"
                                    placeholder="Enter brief description"
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Content
                                </label>
                                <textarea
                                    name="content"
                                    value={formData.content}
                                    onChange={handleChange}
                                    required
                                    rows="6"
                                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors resize-none"
                                    placeholder="Enter article content"
                                />
                            </div>

                            <button
                                type="submit"
                                disabled={isSubmitting}
                                className="w-full flex items-center justify-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-lg font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                {isSubmitting ? (
                                    "Publishing..."
                                ) : (
                                    <>
                                        <Send className="w-5 h-5" />
                                        Publish Article
                                    </>
                                )}
                            </button>
                        </form>
                    </div>
                </div>
            </div>
            <Footer />
        </>
    );
};

export default AddCareerAdvice;